/* ============================================================
   CULT RACING — PHOTO LIGHTBOX  (HAVOC drop page)

   Solves a problem the mobile auto-reveal created: on a phone the
   lot flips to the back print by itself when it centres, and there
   was then no way to get back to the front, and no way to see a
   print bigger than a rack cell.

   ADDITIVE ON PURPOSE. This file is loaded AFTER drop-page.js and
   touches nothing that file owns — no cart, no auto-reveal, no
   deferred back images. drop-page.js and drop-lot.liquid are SHARED
   with the Black Sheep page, so editing either would have changed a
   drop that is meant to stay frozen. Everything here is new surface,
   which is also why promoting this to every drop later is just
   loading this script on the other pages.

   NO MARKUP CHANGE EITHER. The full photo set is read from Shopify's
   own /products/<handle>.js at open time, using the data-handle the
   lot already carries. That means it shows ALL of a product's images,
   not just the two the rack renders, and a product that gains a third
   photo needs no code change.

   Swipe is native CSS scroll-snap, not a JS transform. That is the
   house lesson from the homepage reel: the JS-pinned version janked
   on iOS even on a 17 Pro Max, and the browser's own momentum scroll
   does not.
   ============================================================ */
(function () {
  "use strict";

  var lots = [].slice.call(document.querySelectorAll(".lot"));
  if (!lots.length) return;

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var cache = {};       // handle -> array of image urls
  var box, track, titleEl, countEl, hintEl, prevBtn, nextBtn;
  var slides = [], lastFocus = null, hintDone = false;

  /* ---------- build the dialog once, lazily ---------- */
  function build() {
    if (box) return;
    box = document.createElement("div");
    box.className = "lbx";
    box.id = "lbx";
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", "Product photos");
    /* `hidden` works here only because the stylesheet carries
       [hidden]{display:none !important} near the top. .lbx sets a
       display, and any author display rule beats the UA [hidden] rule.
       That exact trap already shipped once on the homepage form. */
    box.hidden = true;
    box.innerHTML =
      '<div class="lbx__bar">' +
        '<span class="lbx__title"></span>' +
        '<span class="lbx__count" aria-hidden="true"></span>' +
        '<button class="lbx__close" type="button" aria-label="Close photos">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="lbx__track" tabindex="0"></div>' +
      '<button class="lbx__nav lbx__nav--prev" type="button" aria-label="Previous photo">' +
        '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M15 4L7 12l8 8" fill="none" stroke="currentColor" stroke-width="2"/></svg>' +
      '</button>' +
      '<button class="lbx__nav lbx__nav--next" type="button" aria-label="Next photo">' +
        '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" stroke-width="2"/></svg>' +
      '</button>' +
      '<p class="lbx__hint" aria-hidden="true">Swipe</p>';
    document.body.appendChild(box);

    track   = box.querySelector(".lbx__track");
    titleEl = box.querySelector(".lbx__title");
    countEl = box.querySelector(".lbx__count");
    hintEl  = box.querySelector(".lbx__hint");
    prevBtn = box.querySelector(".lbx__nav--prev");
    nextBtn = box.querySelector(".lbx__nav--next");

    box.querySelector(".lbx__close").addEventListener("click", close);
    prevBtn.addEventListener("click", function () { step(-1); });
    nextBtn.addEventListener("click", function () { step(1); });
    track.addEventListener("scroll", onScroll, { passive: true });
    /* backdrop click closes, but only when the tap misses the photo */
    track.addEventListener("click", function (e) {
      if (e.target === track || e.target.classList.contains("lbx__slide")) close();
    });
    document.addEventListener("keydown", onKey);
  }

  /* ---------- Shopify CDN sizing ---------- */
  function sized(url, w) {
    if (!url) return url;
    if (url.indexOf("//") === 0) url = "https:" + url;
    return url + (url.indexOf("?") > -1 ? "&" : "?") + "width=" + w;
  }

  function render(urls, startAt) {
    track.innerHTML = "";
    slides = urls.map(function (u, i) {
      var fig = document.createElement("div");
      fig.className = "lbx__slide";
      var img = document.createElement("img");
      img.src = sized(u, 1400);
      img.alt = "";                       // the dialog is already labelled
      img.decoding = "async";
      /* only the opening photo is urgent; the rest can wait */
      img.loading = i === startAt ? "eager" : "lazy";
      fig.appendChild(img);
      track.appendChild(fig);
      return fig;
    });
    var many = urls.length > 1;
    prevBtn.hidden = nextBtn.hidden = !many;
    hintEl.hidden = !many || hintDone;
    countEl.textContent = many ? (startAt + 1) + " / " + urls.length : "";
    goTo(startAt, true);
  }

  function goTo(i, instant) {
    if (!track.clientWidth) return;
    track.scrollTo({ left: i * track.clientWidth, behavior: (instant || reduce) ? "auto" : "smooth" });
    syncCount(i);
  }

  function index() {
    return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  }

  /* Round robin. Most products here carry exactly two photos, so an arrow
     that dead-ends on the last one reads as broken.

     Whether the move ANIMATES is decided by how far it travels, not by
     whether it wrapped. That distinction matters: with two photos, wrapping
     from the last back to the first is a single slide — the same distance as
     any ordinary step — so treating "wrapped" as "jump instantly" made 2 -> 1
     snap while 1 -> 2 glided. Reported by Mudit, and it was the wrong test.

     Only a wrap that would sweep the whole track backwards past every photo
     jumps, which is what the instant case was actually for. */
  function step(d) {
    var last = slides.length - 1, cur = index(), n = cur + d;
    if (n > last) n = 0;
    else if (n < 0) n = last;
    goTo(n, Math.abs(n - cur) > 1);
    dismissHint();
  }

  function syncCount(i) {
    if (slides.length < 2) return;
    countEl.textContent = (i + 1) + " / " + slides.length;
    /* deliberately no disabled state on the arrows any more: with wrapping
       there is no end to hit. Swiping still stops at the last photo, which is
       what a native scroll should do; the arrows are the loop. */
  }

  var scrollTimer;
  function onScroll() {
    dismissHint();
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(function () { syncCount(index()); }, 60);
  }

  function dismissHint() {
    if (hintDone || !hintEl) return;
    hintDone = true;
    hintEl.hidden = true;
  }

  /* ---------- open ---------- */
  function open(lot, startOnBack) {
    build();
    var handle = lot.getAttribute("data-handle");
    var titleNode = lot.querySelector(".lot__title");
    titleEl.textContent = titleNode ? titleNode.textContent.trim() : "";

    /* Draw instantly from the images already in the rack, so the
       viewer never opens empty while a request is in flight. Same
       optimistic pattern the cart drawer uses. */
    var local = [].slice.call(lot.querySelectorAll(".lot__media img"))
      .map(function (im) { return im.getAttribute("src") || im.getAttribute("data-src"); })
      .filter(Boolean);
    var start = startOnBack && local.length > 1 ? 1 : 0;

    lastFocus = document.activeElement;
    box.hidden = false;
    document.documentElement.classList.add("lbx-open");
    render(local, start);
    box.querySelector(".lbx__close").focus();

    /* Then upgrade to the product's FULL photo set. Products here
       carry two, but accessories and future drops may carry more,
       and this needs no Liquid change to pick them up. */
    if (!handle) return;
    if (cache[handle]) { upgrade(cache[handle], start); return; }
    fetch("/products/" + handle + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.images || !p.images.length) return;
        cache[handle] = p.images;
        upgrade(p.images, start);
      })
      .catch(function () { /* the local pair is already usable */ });
  }

  function upgrade(urls, start) {
    if (box.hidden) return;
    if (urls.length === slides.length) return;   // nothing new to show
    render(urls, Math.min(start, urls.length - 1));
  }

  function close() {
    if (!box || box.hidden) return;
    box.hidden = true;
    document.documentElement.classList.remove("lbx-open");
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) {
    if (!box || box.hidden) return;
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    if (e.key === "ArrowLeft")  { e.preventDefault(); step(-1); }
  }

  /* ---------- wire the rack ---------- */
  lots.forEach(function (lot) {
    var media = lot.querySelector(".lot__media");
    if (!media) return;

    media.addEventListener("click", function (e) {
      /* never swallow a size tap: the buy layer sits on top of the
         bottom of the photo and is the whole point of the card */
      if (e.target.closest(".lot__buy")) return;
      /* open on whichever face the visitor is actually looking at, so
         the mobile auto-flip carries through instead of resetting */
      open(lot, lot.classList.contains("is-active") || lot.matches(":hover"));
    });

    /* keyboard parity: the photo is now interactive, so it has to be
       reachable and operable without a pointer */
    media.setAttribute("role", "button");
    media.setAttribute("tabindex", "0");
    var t = lot.querySelector(".lot__title");
    media.setAttribute("aria-label", "View photos" + (t ? ": " + t.textContent.trim() : ""));
    media.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(lot, false); }
    });
  });

  /* keep the current photo centred when the phone rotates */
  window.addEventListener("resize", function () {
    if (box && !box.hidden) goTo(index(), true);
  });
})();
